import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Coins, History } from "lucide-react";
import { useLocation } from "wouter";
import { DepositModal } from "@/components/deposit-modal";
import { WalletMultiButton } from "@/components/solana-provider";
import React from 'react';

const preferencesSchema = z.object({
  displayName: z.string().optional(),
  avatarUrl: z.string().url().optional(),
});

type PreferencesForm = z.infer<typeof preferencesSchema>;

export default function ProfilePage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [depositModalOpen, setDepositModalOpen] = useState(false);

  const { data: user } = useQuery({
    queryKey: ["/api/auth/user"],
  });

  const form = useForm<PreferencesForm>({
    resolver: zodResolver(preferencesSchema),
    defaultValues: {
      displayName: user?.displayName || "",
      avatarUrl: user?.avatarUrl || "",
    },
    values: {
      displayName: user?.displayName || "",
      avatarUrl: user?.avatarUrl || "",
    },
  });

  const updatePreferences = useMutation({
    mutationFn: async (values: PreferencesForm) => {
      const res = await fetch("/api/user/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user/preferences"] });
      toast({ title: "Profile updated successfully" });
    },
    onError: (error) => {
      toast({
        title: "Error updating profile",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Redirect to home if not authenticated
  React.useEffect(() => {
    if (user === null) {
      setLocation("/");
    }
  }, [user, setLocation]);

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Cacatais Balance Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5" />
              Cacatais Balance
            </CardTitle>
            <CardDescription>
              Manage your cacatais balance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <h4 className="text-sm font-medium">Current Balance</h4>
                  <p className="text-2xl font-bold">{user.cacatais || 0} cacatais</p>
                  <div className="flex flex-col">
                    <Button
                      variant="link"
                      className="pl-0 flex items-center gap-2 text-blue-500 h-6"
                      onClick={() => setLocation("/deposits")}
                    >
                      <History className="h-4 w-4" />
                      View Deposit History
                    </Button>
                    <Button
                      variant="link"
                      className="pl-0 flex items-center gap-2 text-blue-500 h-6"
                      onClick={() => setLocation("/cacatais-transactions")}
                    >
                      <Coins className="h-4 w-4" />
                      View Cacatais Transactions
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => setDepositModalOpen(true)}
                  >
                    Deposit
                  </Button>
                  <Button className='hidden' variant="outline">
                    Withdraw
                  </Button>
                </div>
              </div>
              <div className="mt-4">
                <WalletMultiButton className="w-full" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Settings Card */}
        <Card>
          <CardHeader>
            <CardTitle>Profile Settings</CardTitle>
            <CardDescription>
              Manage your profile information
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit((values) =>
                  updatePreferences.mutate(values)
                )}
                className="space-y-6"
              >
                <FormField
                  control={form.control}
                  name="displayName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Display Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormDescription>
                        This is your public display name
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="avatarUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Avatar URL</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormDescription>
                        URL to your profile picture
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full"
                  disabled={updatePreferences.isPending}
                >
                  Save Changes
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>

        <DepositModal 
          open={depositModalOpen}
          onOpenChange={setDepositModalOpen}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
          }}
        />
      </div>
    </div>
  );
}